import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";

import type { Level, AnswerResult, LevelCompletePayload } from "./levelTypes";
import { ProgressBar } from "./ui/ProgressBar";
import { TimerBar } from "./ui/TimerBar";
import { ResultScreen } from "./ui/ResultScreen";
import { WordMatch } from "./exercises/WordMatch";
import { Translation } from "./exercises/Translation";
import { SentenceBuilder } from "./exercises/SentenceBuilder";
import { MultipleChoice } from "./exercises/MultipleChoice";
import "./LevelPlayer.css";

interface Props {
  level: Level;
  onComplete: (payload: LevelCompletePayload) => void;
  onExit?: () => void;
}

// ─── Star thresholds ──────────────────────────────────────────────────────────
function calcStars(results: AnswerResult[]): 1 | 2 | 3 {
  if (results.length === 0) return 1;
  const score = results.reduce((sum, r) => {
    if (r === "correct") return sum + 1;
    if (r === "hinted") return sum + 0.5;
    return sum;
  }, 0);
  const pct = score / results.length;
  if (pct >= 0.95) return 3;
  if (pct >= 0.60) return 2;
  return 1;
}

// ─── Collect words learned from correct/hinted answers ────────────────────────
function collectWords(level: Level, results: AnswerResult[]): string[] {
  const questions = level.questions as Array<{ word?: string }>;
  return questions
    .filter((_, i) => results[i] === "correct" || results[i] === "hinted")
    .map((q) => q.word ?? "")
    .filter(Boolean);
}

// ─── Slide transition ─────────────────────────────────────────────────────────
const slideVariants = {
  initial: { opacity: 0, x: 48, scale: 0.97 },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 280, damping: 26 },
  },
  exit: {
    opacity: 0,
    x: -40,
    scale: 0.97,
    transition: { duration: 0.16 },
  },
};

// ─── Feedback banner ──────────────────────────────────────────────────────────
interface FeedbackProps {
  kind: "correct" | "wrong";
  onNext: () => void;
}

function FeedbackBanner({ kind, onNext }: FeedbackProps) {
  const isCorrect = kind === "correct";
  return (
    <motion.div
      className={`feedback-banner feedback-banner--${kind}`}
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 80, opacity: 0 }}
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
    >
      <span className="feedback-banner__icon">{isCorrect ? "✅" : "❌"}</span>
      <div className="feedback-banner__text">
        <p className="feedback-banner__title">
          {isCorrect ? "Riktig!" : "Feil svar"}
        </p>
        <p className="feedback-banner__sub">
          {isCorrect ? "Godt jobbet!" : "Øv litt mer på dette."}
        </p>
      </div>
      <button className="feedback-banner__next" onClick={onNext}>
        {isCorrect ? "Neste →" : "Fortsett"}
      </button>
    </motion.div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export function LevelPlayer({ level, onComplete, onExit }: Props) {
  const questions = level.questions;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<AnswerResult[]>([]);
  const [pendingResult, setPendingResult] = useState<AnswerResult | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(level.timeLimit ?? 0);
  const [timedOut, setTimedOut] = useState(false);
  const [payload, setPayload] = useState<LevelCompletePayload | null>(null);

  const hasTimer = typeof level.timeLimit === "number";

  // ── Timer countdown ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasTimer || isComplete) return;

    const id = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          setTimedOut(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [hasTimer, isComplete]);

  // When time runs out, finish the level immediately
  useEffect(() => {
    if (timedOut && !isComplete) {
      finishLevel(results);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut]);

  // ── Answer handling ──────────────────────────────────────────────────────────
  const handleAnswer = useCallback(
    (result: AnswerResult) => {
      setPendingResult(result);
    },
    []
  );

  function advanceToNext() {
    if (pendingResult === null) return;
    const newResults = [...results, pendingResult];
    setResults(newResults);
    setPendingResult(null);

    if (currentIndex + 1 >= questions.length) {
      finishLevel(newResults);
    } else {
      setCurrentIndex((i) => i + 1);
    }
  }

  function finishLevel(finalResults: AnswerResult[]) {
    const stars = calcStars(finalResults);
    const correctCount = finalResults.filter(
      (r) => r === "correct" || r === "hinted"
    ).length;
    const isPerfect = correctCount === questions.length && finalResults.every((r) => r === "correct");
    const inTime = hasTimer && !timedOut;

    let gold = level.goldReward.base;
    if (isPerfect && level.goldReward.perfect) gold += level.goldReward.perfect;
    if (inTime && level.goldReward.timed) gold += level.goldReward.timed;

    const wordsLearned = collectWords(level, finalResults);
    const p: LevelCompletePayload = { levelId: level.id, stars, goldEarned: gold, wordsLearned };
    setPayload(p);
    setIsComplete(true);
    onComplete(p);
  }

  function handlePlayAgain() {
    setCurrentIndex(0);
    setResults([]);
    setPendingResult(null);
    setIsComplete(false);
    setTimedOut(false);
    setTimeLeft(level.timeLimit ?? 0);
    setPayload(null);
  }

  // ── Result screen ────────────────────────────────────────────────────────────
  if (isComplete && payload) {
    return (
      <ResultScreen
        payload={payload}
        levelTitle={level.title}
        onPlayAgain={handlePlayAgain}
        onNext={() => onExit?.()}
      />
    );
  }

  // ── Current question ─────────────────────────────────────────────────────────
  const currentQ = questions[currentIndex];

  function renderExercise() {
    switch (level.type) {
      case "wordmatch":
        return (
          <WordMatch
            question={level.questions[currentIndex]}
            allQuestions={level.questions}
            onAnswer={handleAnswer}
          />
        );
      case "translation":
        return (
          <Translation
            question={level.questions[currentIndex]}
            onAnswer={handleAnswer}
          />
        );
      case "sentencebuilder":
        return (
          <SentenceBuilder
            question={level.questions[currentIndex]}
            onAnswer={handleAnswer}
          />
        );
      case "multiplechoice":
        return (
          <MultipleChoice
            question={level.questions[currentIndex]}
            onAnswer={handleAnswer}
          />
        );
    }
  }

  return (
    <div className="level-page">
      {/* Header */}
      <header className="level-page__header">
        {onExit && (
          <button
            className="level-page__back"
            onClick={onExit}
            aria-label="Tilbake til kartet"
          >
            ←
          </button>
        )}
        <span className="level-page__title">{level.title}</span>
      </header>

      {/* Progress */}
      <ProgressBar
        current={currentIndex + (pendingResult !== null ? 1 : 0)}
        total={questions.length}
      />

      {/* Timer */}
      {hasTimer && (
        <TimerBar timeLeft={timeLeft} total={level.timeLimit!} />
      )}

      {/* Exercise area */}
      <main className="level-page__body">
        <AnimatePresence mode="wait">
          <motion.div
            key={`q-${currentIndex}`}
            variants={slideVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            {currentQ && renderExercise()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Feedback banner */}
      <AnimatePresence>
        {pendingResult !== null && (
          <FeedbackBanner
            kind={pendingResult === "wrong" ? "wrong" : "correct"}
            onNext={advanceToNext}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
